import React, { useContext, useEffect, useState } from 'react';
import {
  Expense,
  ExpenseCategory,
} from '../../../../interfaces/delivery-company/expenses';
import { emptyExpense } from './utils';
import { useSnackBar } from '../../../../contexts/snackbar';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import {
  Typography,
  LinearProgress,
  Alert,
  FormControl,
  TextField,
  Button,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Switch,
} from '@mui/material';
import { isMobile, isTablet } from 'react-device-detect';
import { CompactMerchantType } from '../../../../interfaces/delivery-company/merchants';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import MoneyInput from '../../../common/money-input/money-input';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Backspace } from '@mui/icons-material';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { format } from 'date-fns';
import FilterChips from '../../../common/filter-chips/filter-chips';

interface ExpenseFormProps {
  defaultExpense?: Expense;
  onSuccess: (e: Expense) => void;
}


function ExpenseForm({ defaultExpense, onSuccess }: ExpenseFormProps) {
  const [expenseData, setExpenseData] = useState<Expense>(
    defaultExpense || emptyExpense
  );
  const { showMessage } = useSnackBar();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const { config } = useContext(AuthContext);
  const [creatingNewLabel, setCreatingNewLabel] = useState(false);
  const [savingLabel, setSavingLabel] = useState(false);
  const [labelInput, setLabelInput] = useState<string>('');
  
  const [payNow, setPayNow] = useState(false)

  const [dueDate, setDueDate] = useState<Dayjs | null>(
    defaultExpense?.due_date ? dayjs(new Date(defaultExpense.due_date)) : null
  );

  const [paymentDate, setPaymentDate] = useState<Dayjs | null>(
    defaultExpense?.paid_at ? dayjs(new Date(defaultExpense.paid_at)) : null
  );

  useEffect(()=>{
    if (!payNow) setPaymentDate(null);
  }, [payNow])

  const handleSaveNewLabel = async () => {
    setSavingLabel(true);
    try {
      const response = await axios.post<ExpenseCategory>(
        `${API_ENDPOINT}/v1/expense-categories/`,
        { label: labelInput },
        config
      );
      setLabelInput('');
      setExpenseData({ ...expenseData, category: response.data });
      showMessage('Label created successfully');
      setCreatingNewLabel(false);
    } catch (e) {
      showMessage('Failed to create label. Please try again.', 'error');
    } finally {
      setSavingLabel(false);
    }
  };
  useEffect(() => {
    setExpenseData({
      ...expenseData,
      due_date: dueDate ? format(dueDate.toDate(), 'yyyy-MM-dd') : undefined,
    });
  }, [dueDate]);

  useEffect(() => {
    setExpenseData({
      ...expenseData,
      paid_at: paymentDate ? format(paymentDate.toDate(), "yyyy-MM-dd'T'HH:mm:ss") : undefined,
    });
  }, [paymentDate]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setMessage('');
      setResponse(undefined);
      let response;
      if (defaultExpense == undefined) {
        response = await axios.post<Expense>(
          `${API_ENDPOINT}/v1/expenses/`,
          expenseData,
          config
        );
      } else {
        response = await axios.put<Expense>(
          `${API_ENDPOINT}/v1/expenses/${defaultExpense.id}/`,
          expenseData,
          config
        );
      }

      showMessage('Expense Saved Successfully');
      onSuccess(response.data);
    } catch (e) {
      showMessage('Failed to save expense.', 'error');
      if (isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to save expense' +
            (e.response?.statusText ? e.response.statusText : '')
        );
      } else {
        setMessage('Failed to save expense');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchLabels = async () => {
    try {
      return (
        await axios.get<{ count: number; results: ExpenseCategory[] }>(
          `${API_ENDPOINT}/v1/expense-categories/`,
          config
        )
      ).data.results;
    } catch (e) {
      showMessage('Failed to load labels', 'error');
    }
    return [];
  };

  return (
    <Box>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        {expenseData.id == 0 ? 'Create Expense' : 'Edit Expense'}
      </Typography>
      {isLoading ? (
        <>
          Saving Expense... <br /> <LinearProgress />
        </>
      ) : (
        <>
          {message != '' && (
            <>
              <Alert
                severity={
                  response && response.status >= 200 && response.status < 300
                    ? 'success'
                    : 'error'
                }
              >
                <>
                  {message}

                  {response && response.status >= 300 && (
                    <ErrorsFlattenner rawErrors={response.data} />
                  )}
                </>
              </Alert>
              <br />{' '}
            </>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <AutocompleteWithThrottle
              label="Expense Category"
              fetch={handleFetchLabels}
              fetchAllOnLoad
              disabled={isLoading}
              onClickAdd={() => setCreatingNewLabel(true)}
              value={expenseData.category != null ? expenseData.category : null}
              getOptionLabel={(option: ExpenseCategory) => option.label}
              onChange={(e, value) => {
                const expenseLabel = value;
                if (expenseLabel != null)
                  setExpenseData({
                    ...expenseData,
                    category: expenseLabel,
                  });
                else {
                  setExpenseData({
                    ...expenseData,
                    category: undefined,
                  });
                }
              }}
            />
            <br />

            <TextField
              value={expenseData.description}
              onChange={(e) =>
                setExpenseData({ ...expenseData, description: e.target.value })
              }
              label={'Description'}
              fullWidth
              multiline
              minRows={4}
              placeholder="Extra description for auditing"
            />
            <br />
            <br />

            <MoneyInput
              money={expenseData.expense}
              disabled={isLoading}
              setMoney={(m) => setExpenseData({ ...expenseData, expense: m })}
              label={'Expense Amount'}
            />

            <br />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <FormControl fullWidth>
                  <DatePicker
                    disabled={isLoading}
                    label="Due Date"
                    value={dueDate}
                    onChange={(newValue) => {
                      if (dayjs(newValue).isValid()) {
                        setDueDate(dayjs(newValue));
                      } else {
                        setDueDate(null);
                      }
                    }}
                  />
                </FormControl>

                <IconButton onClick={() => setDueDate(null)}>
                  <Backspace color={'primary'} />
                </IconButton>
              </div>
                  

                <Box mt={2}> 
                  <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
                    <Switch checked={payNow} onChange={()=>setPayNow(!payNow)} /> Mark as Paid
                  </Box>
                  
                  {payNow && (
                    <FormControl fullWidth>
                      <DateTimePicker
                        disabled={isLoading}
                        label="Payment Date & Time"
                        value={paymentDate}
                        onChange={(newValue) => {
                          if (newValue && dayjs(newValue).isValid()) {
                            setPaymentDate(dayjs(newValue));
                          } else {
                            setPaymentDate(null);
                          }
                        }}
                        slotProps={{
                          textField: { fullWidth: true },
                        }}
                      />
                    </FormControl>
                  )}


                </Box>

                
              
            </LocalizationProvider>
            <br />
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                size={(isMobile && !isTablet) ? 'small' : 'medium'}
                type="submit"
                variant="contained"
                disabled={isLoading}
              >
                Save Expense{' '}
                {isLoading && <CircularProgress sx={{ ml: 2 }} size={20} />}
              </Button>
            </div>
          </form>{' '}
        </>
      )}

      {creatingNewLabel && (
        <CustomDialog open={true} onClose={() => {setLabelInput(""); setCreatingNewLabel(false)}}>
          <DialogTitle>New Expense Category</DialogTitle>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveNewLabel();
              }}
            >
              <br />
              <FormControl fullWidth>
                <TextField
                  required
                  label="Category Label"
                  placeholder="Enter category label"
                  value={labelInput}
                  onChange={(e) => setLabelInput(e.target.value)}
                  disabled={savingLabel}
                ></TextField>
              </FormControl>

              <br />
              <br />
              <div style={{ width: '100%', textAlign: 'center' }}>
                <Button
                  disabled={savingLabel}
                  variant="contained"
                  type="submit"
                >
                  Save{' '}
                  {savingLabel && <CircularProgress sx={{ ml: 2 }} size={20} />}
                </Button>
              </div>
            </form>
          </DialogContent>
        </CustomDialog>
      )}
    </Box>
  );
}

export default ExpenseForm;
